import React, { useCallback, useEffect, useMemo } from 'react'

import { useForm, useStore } from '@tanstack/react-form'
import cn from 'classnames'
import {
  AuthButton,
  AuthButtonColor,
  AuthButtonVariant,
  AuthCheckbox,
  AuthInput,
  AuthSuccess
} from 'modules/auth/components'
import { Trans, useTranslation } from 'react-i18next'
import { Navigate, useNavigate } from 'react-router-dom'
import { useRegisterMutation } from 'shared/api/auth/requests'
import loginBg from 'shared/assets/backgrounds/login-bg.webp'
import { PasswordRequirements } from 'shared/components/PasswordRequirements'
import { FEATURE_FLAGS } from 'shared/constants/featureFlags'
import { useSearchParams } from 'shared/hooks/useSearchParams'
import { getFieldError } from 'shared/utils'

import { AuthPageTemplate } from '../../templates'
import styles from './RegisterPage.module.scss'
import { getRegisterSchema } from './RegisterPage.schema'

// TODO: clarify with Franz how to determine if it is personal or organization
// account

export const RegisterPage = () => {
  const { t, i18n } = useTranslation('auth', { keyPrefix: 'register' })
  const navigate = useNavigate()
  const currentLocale = i18n?.resolvedLanguage ?? i18n?.language ?? 'en'
  const landingDomain = process.env.REACT_APP_LANDING_DOMAIN

  const [{ invitation }] = useSearchParams<{ invitation?: string }>()

  const registerMutation = useRegisterMutation()
  const registerSchema = useMemo(() => getRegisterSchema(t), [t])

  useEffect(() => {
    if (registerMutation.isSuccess) {
      const timeoutId = setTimeout(() => {
        navigate('/login')
      }, 5000)

      return () => clearTimeout(timeoutId)
    }
  }, [registerMutation.isSuccess, navigate])

  const form = useForm({
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      confirmPassword: '',
      terms: false
    },
    validators: {
      onMount: registerSchema,
      onChange: registerSchema
    },
    onSubmit: ({ value }) => {
      registerMutation.mutate({
        account_type: 'personal',
        first_name: value.firstName,
        last_name: value.lastName,
        email: value.email,
        password: value.password,
        repeat_password: value.confirmPassword
      })
    }
  })

  const isSubmitted = useStore(
    form.store,
    state => state.submissionAttempts > 0
  )

  const isEmailExistsError = registerMutation.error?.response?.status === 400

  const handleLinkClick = useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>) => {
      event.stopPropagation()
    },
    []
  )

  const termsOfServiceUrl = `${landingDomain}/terms-and-conditions?l=${currentLocale}`
  const privacyPolicyUrl = `${landingDomain}/privacy-policy?l=${currentLocale}`

  if (!FEATURE_FLAGS.REGISTER) {
    return <Navigate to='/' replace />
  }

  return (
    <AuthPageTemplate backgroundImageUrl={loginBg}>
      {!registerMutation.isSuccess ? (
        <div className={styles.registerPage}>
          <h1 className={styles.title}>{t('title')}</h1>
          <form
            className={styles.form}
            onSubmit={e => {
              e.preventDefault()
              form.handleSubmit()
            }}
          >
            <div className={cn(styles.email, styles.formItem)}>
              <form.Field name='email'>
                {field => {
                  const validationError = getFieldError(field, {
                    isSubmitted,
                    requireTouched: true
                  })
                  const apiError = isEmailExistsError
                    ? registerMutation.error?.response?.data?.message
                    : undefined
                  const error = apiError || validationError
                  return (
                    <AuthInput
                      placeholder={t('form.email_placeholder')}
                      type='email'
                      name='email'
                      onChange={e => {
                        field.handleChange(e.target.value)
                        if (isSubmitted) {
                          field.validate('change')
                        }
                      }}
                      value={field.state.value}
                      error={error}
                    />
                  )
                }}
              </form.Field>
            </div>
            <div className={cn(styles.firstName, styles.formItem)}>
              <form.Field name='firstName'>
                {field => {
                  const error = getFieldError(field, {
                    isSubmitted,
                    requireTouched: true
                  })
                  return (
                    <AuthInput
                      placeholder={t('form.first_name_placeholder')}
                      type='text'
                      name='firstName'
                      onChange={e => {
                        field.handleChange(e.target.value)
                        if (isSubmitted) {
                          field.validate('change')
                        }
                      }}
                      value={field.state.value}
                      error={error}
                    />
                  )
                }}
              </form.Field>
            </div>
            <div className={cn(styles.lastName, styles.formItem)}>
              <form.Field name='lastName'>
                {field => {
                  const error = getFieldError(field, {
                    isSubmitted,
                    requireTouched: true
                  })
                  return (
                    <AuthInput
                      placeholder={t('form.last_name_placeholder')}
                      type='text'
                      name='lastName'
                      onChange={e => {
                        field.handleChange(e.target.value)
                        if (isSubmitted) {
                          field.validate('change')
                        }
                      }}
                      value={field.state.value}
                      error={error}
                    />
                  )
                }}
              </form.Field>
            </div>
            <div className={cn(styles.password, styles.formItem)}>
              <form.Field name='password'>
                {field => {
                  const error = getFieldError(field, {
                    isSubmitted,
                    requireTouched: true
                  })
                  return (
                    <>
                      <AuthInput
                        placeholder={t('form.password_placeholder')}
                        type='password'
                        name='password'
                        showTogglePassword
                        onChange={e => {
                          field.handleChange(e.target.value)
                          if (isSubmitted) {
                            field.validate('change')
                          }
                        }}
                        value={field.state.value}
                        error={error}
                      />
                      <PasswordRequirements password={field.state.value} />
                    </>
                  )
                }}
              </form.Field>
            </div>
            <div className={cn(styles.confirmPassword, styles.formItem)}>
              <form.Field name='confirmPassword'>
                {field => {
                  const error = getFieldError(field, {
                    isSubmitted,
                    requireTouched: true
                  })
                  return (
                    <AuthInput
                      placeholder={t('form.confirm_password_placeholder')}
                      type='password'
                      name='confirmPassword'
                      showTogglePassword
                      onChange={e => {
                        field.handleChange(e.target.value)
                        if (isSubmitted) {
                          field.validate('change')
                        }
                      }}
                      value={field.state.value}
                      error={error}
                    />
                  )
                }}
              </form.Field>
            </div>
            <div className={cn(styles.confirm, styles.formItem)}>
              <form.Field name='terms'>
                {field => {
                  const error = getFieldError(field, {
                    isSubmitted,
                    requireTouched: true
                  })
                  return (
                    <AuthCheckbox
                      label={
                        <Trans
                          ns='auth'
                          i18nKey='register.form.accept_terms_label'
                          components={{
                            terms: (
                              <a
                                href={termsOfServiceUrl}
                                target='_blank'
                                rel='noopener noreferrer'
                                onClick={handleLinkClick}
                                className={styles.link}
                              />
                            ),
                            privacy: (
                              <a
                                href={privacyPolicyUrl}
                                target='_blank'
                                rel='noopener noreferrer'
                                onClick={handleLinkClick}
                                className={styles.link}
                              />
                            )
                          }}
                        />
                      }
                      onChange={e => {
                        field.handleChange(e.target.checked)
                        if (isSubmitted) {
                          field.validate('change')
                        }
                      }}
                      checked={field.state.value}
                      error={error}
                    />
                  )
                }}
              </form.Field>
            </div>
            <div className={cn(styles.submit, styles.formItem)}>
              {!!invitation && (
                <div className={styles.invitation}>
                  <p className={styles.message}>
                    <Trans
                      ns='auth'
                      i18nKey='register.form.invitation_message'
                      components={{ strong: <strong /> }}
                    />
                  </p>
                  <AuthCheckbox label='Join' />
                </div>
              )}
              <AuthButton
                label={t('form.register_button')}
                variant={AuthButtonVariant.GHOST}
                colorType={AuthButtonColor.GHOST_DARKER}
                isLoading={registerMutation.isPending}
                type='submit'
              />
            </div>
          </form>
        </div>
      ) : (
        <AuthSuccess
          title={t('success.heading')}
          description={t('success.description')}
        />
      )}
    </AuthPageTemplate>
  )
}
